import type { ComponentType, LazyExoticComponent } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { NavKey } from '@/i18n';

/** Permission resource(s): readable when any of them is (e.g. "admin", or ["inventory.part", "inventory.part.transit"]). */
export type Resource = string | readonly string[];

/** One tab of a hub page, selected by `?tab=<key>` (the first visible tab has no parameter). */
export interface FeatureTab {
  key: string;
  /** Translation key under shell:nav (e.g. 'sharedFleet'); see useNavTitle(). */
  title: NavKey;
  resource?: Resource;
  component: LazyExoticComponent<ComponentType>;
  /** Former standalone path of this screen; it redirects here so old links and bookmarks keep working. */
  legacyPath?: string;
}

/** One screen of the console: a single page (`component`) or a hub of related tabs (`tabs`). */
export interface FeatureRoute {
  /** Absolute path, e.g. /platform/admins. */
  path: string;
  /** Menu / page title: a translation key under shell:nav (e.g. 'rides'); see useNavTitle(). */
  title: NavKey;
  /**
   * Permission resource(s) gating the screen, as in the legacy console's route `authority`.
   * A hub without one is visible when any of its tabs is.
   */
  resource?: Resource;
  component?: LazyExoticComponent<ComponentType>;
  tabs?: FeatureTab[];
  /** Tabs promoted to standalone pages; old ?tab= links redirect before checking hub permissions. */
  movedTabs?: Record<string, string>;
  /** Hidden from the menu (detail pages). */
  hideInMenu?: boolean;
  /** Owns its market filters; retain its state when the header working market changes. */
  marketIndependent?: boolean;
  /** Former path of this screen; it redirects here. */
  legacyPath?: string;
}

/** A top-level menu section owning several routes. */
export interface FeatureSection {
  key: string;
  /** Translation key under shell:nav. */
  title: NavKey;
  /** Sidebar icon (a lucide icon component, e.g. `Bike`). */
  icon: LucideIcon;
  routes: FeatureRoute[];
}

/** The resources that make a route visible: its own, or else any of its tabs'. */
export function routeResources(route: FeatureRoute): Resource | undefined {
  if (route.resource !== undefined || !route.tabs) return route.resource;
  const all = route.tabs.flatMap((t) => (t.resource === undefined ? [] : typeof t.resource === 'string' ? [t.resource] : [...t.resource]));
  // a tab with no resource is open to everyone, and so is the hub
  return route.tabs.some((t) => t.resource === undefined) ? undefined : [...new Set(all)];
}
